import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Copy, Check, X } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";
import { Chip, Label, Panel, PanelHeader, Reveal, StatusDot } from "../components/primitives";
import { DataStatus } from "../components/DataStatus";
import { apiGet, timeAgo } from "../lib/api";

const SORTS = [["symbol", "Symbol"], ["name", "Name"], ["price", "Price"], ["status", "Status"]];
const short = (a) => (a ? `${a.slice(0, 8)}…${a.slice(-6)}` : "N/A");

const fmtPrice = (p) => {
  if (!p) return "N/A";
  const n = Number(p);
  if (n === 0) return "0";
  if (n >= 1) return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n.toPrecision(4);
};

const QualityChip = ({ q }) =>
  q === "VERIFIED" ? <Chip tone="green">Verified</Chip>
  : q === "STALE" ? <Chip tone="gold">Stale</Chip>
  : q === "REVIEW" ? <Chip tone="gold">Review</Chip>
  : <Chip tone="muted">N/A</Chip>;

const CopyBtn = ({ value, children }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="font-mono text-xs text-fog-600">N/A</span>;
  return (
    <button
      onClick={async (e) => { e.stopPropagation(); try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch {} }}
      className="group flex items-center gap-1.5 font-mono text-xs text-fog-400 transition-colors hover:text-pulse-soft"
      title={value}
    >
      {children || short(value)}
      {copied ? <Check className="h-3 w-3 text-pulse-soft" /> : <Copy className="h-3 w-3 opacity-40 group-hover:opacity-100" />}
    </button>
  );
};

const TokenModal = ({ asset, onClose }) => {
  const [interval, setIntervalv] = useState("5m");
  const [candles, setCandles] = useState(null);
  const [chartState, setChartState] = useState("loading");

  useEffect(() => {
    let dead = false;
    setChartState("loading");
    apiGet(`/prices/${asset.symbol}/candles?interval=${interval}&limit=120`)
      .then((d) => {
        if (dead) return;
        setCandles(d.candles || []);
        setChartState(d.candles?.length ? "ok" : "empty");
      })
      .catch(() => !dead && setChartState("error"));
    return () => { dead = true; };
  }, [asset.symbol, interval]);

  const chartData = (candles || []).map((c) => ({ t: c.startTimestamp, v: Number(c.close) }));
  const ref = asset.reference;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end justify-center bg-ink-900/80 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose} data-testid="token-modal-backdrop"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] w-full max-w-2xl overflow-y-auto border border-white/[0.1] bg-panel"
        data-testid="token-modal"
      >
        <div className="flex items-center justify-between border-b border-white/[0.08] px-6 py-4">
          <div className="flex items-center gap-3">
            {asset.logoUrl && <img src={asset.logoUrl} alt="" className="h-7 w-7 rounded-full" />}
            <div>
              <h3 className="font-mono text-lg font-bold tracking-[0.08em] text-fog-100">{asset.symbol}</h3>
              <span className="font-mono text-[10px] text-fog-600">{asset.name}</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="token-modal-close"
            className="flex h-9 w-9 items-center justify-center border border-white/10 text-fog-400 hover:text-fog-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-5 grid grid-cols-2 gap-3">
            <div className="border border-white/[0.08] bg-ink-900 p-3.5">
              <Label className="mb-1.5">Reference Price (USD)</Label>
              {ref ? (
                <>
                  <span className="font-mono text-xl font-bold text-fog-100" data-testid="token-ref-price">${fmtPrice(ref.price)}</span>
                  <div className="mt-2 flex items-center gap-2">
                    <QualityChip q={ref.quality} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-fog-600">{timeAgo(ref.sourceTimestamp)}</span>
                  </div>
                </>
              ) : (
                <span className="font-mono text-sm text-fog-600">UNAVAILABLE</span>
              )}
            </div>
            <div className="border border-white/[0.08] bg-ink-900 p-3.5">
              <Label className="mb-1.5">Onchain Price</Label>
              {asset.onchain ? (
                <>
                  <span className="font-mono text-xl font-bold text-fog-100">{fmtPrice(asset.onchain.price)} <span className="text-xs text-fog-400">{asset.onchain.quoteCurrency}</span></span>
                  <div className="mt-2 flex items-center gap-2">
                    <QualityChip q={asset.onchain.quality} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-fog-600">{timeAgo(asset.onchain.sourceTimestamp)}</span>
                  </div>
                </>
              ) : (
                <span className="font-mono text-sm text-fog-600">N/A — no indexed pool</span>
              )}
            </div>
          </div>

          <div className="mb-5 space-y-1 border border-white/[0.06] p-3.5 font-mono text-[11px]">
            <div className="flex justify-between py-1"><span className="text-fog-600">CONTRACT</span><CopyBtn value={asset.tokenAddress} /></div>
            <div className="flex justify-between py-1"><span className="text-fog-600">CHAIN</span><span className="text-fog-100">Robinhood Chain · 4663</span></div>
            <div className="flex justify-between py-1"><span className="text-fog-600">SOURCE</span><span className="text-fog-100">{ref?.sourceId || asset.onchain?.sourceId || "—"}</span></div>
            <div className="flex justify-between py-1"><span className="text-fog-600">PRICE TYPE</span><span className="text-fog-100">{ref ? "REFERENCE_PRICE (raw)" : "—"}</span></div>
            <div className="flex justify-between py-1"><span className="text-fog-600">CA MULTIPLIER</span><span className="text-fog-100">{asset.multiplier || "N/A"}</span></div>
            <div className="flex justify-between py-1"><span className="text-fog-600">NORMALIZATION</span><span className="text-brass-bright">RAW ONLY — semantics unverified</span></div>
          </div>

          <div className="mb-2 flex items-center justify-between">
            <Label>Historical — real observations only</Label>
            <div className="flex gap-1" data-testid="chart-intervals">
              {["5m", "15m", "1h", "1d"].map((iv) => (
                <button key={iv} onClick={() => setIntervalv(iv)} data-testid={`interval-${iv}`}
                  className={`border px-2 py-1 font-mono text-[10px] uppercase ${interval === iv ? "border-pulse/50 bg-pulse/10 text-pulse-soft" : "border-white/10 text-fog-600 hover:text-fog-100"}`}>
                  {iv}
                </button>
              ))}
            </div>
          </div>
          <div className="border border-white/[0.08] bg-ink-900 p-3" data-testid="token-chart">
            {chartState === "loading" && <div className="h-40 animate-pulse bg-white/[0.03]" />}
            {chartState === "error" && <div className="flex h-40 items-center justify-center font-mono text-[10px] uppercase tracking-[0.2em] text-red-400">Market data unavailable</div>}
            {chartState === "empty" && (
              <div className="flex h-40 flex-col items-center justify-center gap-2 text-center">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog-600">No historical observations yet</span>
                <span className="max-w-xs text-[11px] text-fog-600">History accumulates from deployment time. No candles are fabricated or interpolated.</span>
              </div>
            )}
            {chartState === "ok" && (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="tok" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00C805" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#00C805" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <YAxis hide domain={["dataMin", "dataMax"]} />
                    <Area type="stepAfter" dataKey="v" stroke="#00C805" strokeWidth={1.5} fill="url(#tok)" isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.15em] text-fog-600">
            {chartState === "ok" ? `${chartData.length} buckets with real observations · gaps preserved · USD` : "Derived from stored observations when present"}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function Market() {
  const [assets, setAssets] = useState(null);
  const [quality, setQuality] = useState(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("symbol");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let dead = false;
    const load = async () => {
      try {
        const [a, q] = await Promise.all([apiGet("/assets?limit=400"), apiGet("/prices")]);
        if (dead) return;
        const bySym = {};
        for (const p of q.assets) bySym[p.symbol] = p;
        setAssets(a.assets.map((t) => ({ ...t, priceInfo: bySym[t.symbol] || null })));
        setQuality(q.quality);
        setError(false);
      } catch {
        if (!dead) setError(true);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { dead = true; clearInterval(id); };
  }, []);

  const rows = useMemo(() => {
    if (!assets) return [];
    const q = query.trim().toLowerCase();
    let list = assets.filter(
      (a) => !q || a.symbol?.toLowerCase().includes(q) || a.name?.toLowerCase().includes(q) || a.contract_address?.toLowerCase().includes(q)
    );
    const priceOf = (a) => { const p = a.priceInfo?.reference?.price; return p ? Number(p) : -1; };
    return [...list].sort((a, b) => {
      if (sort === "name") return (a.name || "").localeCompare(b.name || "");
      if (sort === "status") return (a.status || "").localeCompare(b.status || "");
      if (sort === "price") return priceOf(b) - priceOf(a);
      return (a.symbol || "").localeCompare(b.symbol || "");
    });
  }, [assets, query, sort]);

  return (
    <main className="pt-16" data-testid="market-page">
      <div className="grid-bg border-b border-white/[0.06]">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-end justify-between gap-6 px-5 py-16 md:px-8 md:py-20">
          <Reveal>
            <div className="flex items-center gap-3"><StatusDot /><Label tone="green">Robinhood Stock Tokens — verified sources only</Label></div>
            <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-fog-100 sm:text-5xl">MARKET INTELLIGENCE</h1>
          </Reveal>
          {error ? <DataStatus status="OFFLINE" label="Market Data Unavailable" />
            : assets ? <DataStatus status="LIVE" label="Robinhood Quotes API" sub={`${assets.length} assets`} />
            : <DataStatus status="SYNCING" label="Connecting" />}
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8">
        {quality && (
          <div className="mb-6 grid grid-cols-2 gap-px border border-white/[0.08] bg-white/[0.08] sm:grid-cols-3 lg:grid-cols-6" data-testid="market-metrics">
            {[
              ["Verified Prices", quality.withVerifiedPrices, "text-pulse-soft"],
              ["Stale", quality.withStalePrices, "text-brass-bright"],
              ["Review Flags", quality.withReviewFlags, "text-brass-bright"],
              ["Onchain Prices", quality.withOnchainPrices, "text-pulse-soft"],
              ["Reference Prices", quality.withReferencePrices, "text-fog-100"],
              ["Last Ingestion", quality.lastSuccessfulIngestion ? timeAgo(quality.lastSuccessfulIngestion) : "—", "text-fog-100"],
            ].map(([k, v, c]) => (
              <div key={k} className="bg-panel p-4">
                <Label>{k}</Label>
                <span className={`mt-1.5 block font-mono text-lg font-bold ${c}`}>{v}</span>
              </div>
            ))}
          </div>
        )}

        <Panel>
          <PanelHeader right={
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-fog-600" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…"
                  data-testid="market-search"
                  className="w-36 border border-white/10 bg-ink-900 py-1.5 pl-7 pr-2 font-mono text-[11px] text-fog-100 placeholder:text-fog-600 focus:border-pulse/50 focus:outline-none sm:w-48" />
              </div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="market-sort"
                className="border border-white/10 bg-ink-900 px-2 py-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-fog-100 focus:border-pulse/50 focus:outline-none">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          }>
            Stock Tokens — Reference Prices
          </PanelHeader>

          {error ? (
            <div className="p-10 text-center" data-testid="market-error">
              <DataStatus status="OFFLINE" label="Market Data Unavailable" className="justify-center" />
              <p className="mt-3 text-xs text-fog-600">The market data service could not be reached. No fallback data is shown.</p>
            </div>
          ) : !assets ? (
            <div className="space-y-px p-px" data-testid="market-loading">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-11 animate-pulse bg-white/[0.03]" />)}
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="market-table">
              <table className="w-full min-w-[960px] font-mono text-xs">
                <thead>
                  <tr className="border-b border-white/[0.08] text-left text-[9px] uppercase tracking-[0.2em] text-fog-600">
                    <th className="px-4 py-3">Asset</th>
                    <th className="px-4 py-3">Contract</th>
                    <th className="px-4 py-3 text-right">Ref Price</th>
                    <th className="px-4 py-3 text-right">Bid / Ask</th>
                    <th className="px-4 py-3">Freshness</th>
                    <th className="px-4 py-3 text-right">Updated</th>
                    <th className="px-4 py-3 text-right">Mult</th>
                    <th className="px-4 py-3 text-right">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => {
                    const ref = a.priceInfo?.reference;
                    return (
                      <tr key={a.id} onClick={() => setSelected({
                          symbol: a.symbol, name: a.name, logoUrl: a.logo_url, tokenAddress: a.contract_address,
                          reference: ref, onchain: a.priceInfo?.onchain, multiplier: a.current_multiplier,
                        })}
                        className="cursor-pointer border-b border-white/[0.04] transition-colors hover:bg-white/[0.03]"
                        data-testid={`market-row-${(a.symbol || "").toLowerCase()}`}>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2.5">
                            {a.logo_url ? <img src={a.logo_url} alt="" className="h-5 w-5 rounded-full" loading="lazy" /> : <span className="h-5 w-5 rounded-full bg-white/[0.06]" />}
                            <div>
                              <span className="font-bold text-fog-100">{a.symbol}</span>
                              <span className="block max-w-[200px] truncate text-[10px] text-fog-600">{a.name}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5"><CopyBtn value={a.contract_address} /></td>
                        <td className="px-4 py-2.5 text-right text-fog-100" data-testid={`price-${(a.symbol || "").toLowerCase()}`}>
                          {ref ? `$${fmtPrice(ref.price)}` : <span className="text-fog-600">N/A</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right text-fog-400">
                          {ref?.bid ? `${fmtPrice(ref.bid)} / ${fmtPrice(ref.ask)}` : "—"}
                        </td>
                        <td className="px-4 py-2.5">{ref ? <QualityChip q={ref.quality} /> : <Chip tone="muted">N/A</Chip>}</td>
                        <td className="px-4 py-2.5 text-right text-fog-400">{ref ? timeAgo(ref.sourceTimestamp) : "—"}</td>
                        <td className="px-4 py-2.5 text-right text-fog-400">{a.current_multiplier ? Number(a.current_multiplier).toFixed(4) : "N/A"}</td>
                        <td className="px-4 py-2.5 text-right text-fog-600">{ref ? "RH Quotes" : "—"}</td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-fog-600">No assets match this search</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Panel className="p-5">
            <div className="flex items-center justify-between">
              <Label>Reference vs Onchain</Label>
              <DataStatus status="LIVE" label="Separated" />
            </div>
            <p className="mt-3 text-xs leading-relaxed text-fog-400">
              Reference prices come from the Robinhood quotes API for the underlying instrument. Onchain prices
              come from actual pool reserves on Robinhood Chain. The two are never merged — a token can trade at
              a premium or discount to its reference.
            </p>
          </Panel>
          <Panel className="p-5">
            <div className="flex items-center justify-between">
              <Label>Corporate Actions</Label>
              <DataStatus status="DELAYED" label="Semantics unverified" pulse={false} />
            </div>
            <p className="mt-3 text-xs leading-relaxed text-fog-400">
              Robinhood has not published an authoritative definition of the token multiplier. All prices are
              stored and displayed RAW — no adjusted price is computed until the semantics are verified.
            </p>
          </Panel>
        </div>
      </div>

      <AnimatePresence>
        {selected && <TokenModal asset={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </main>
  );
}
